import { DocLocation } from '../../../../types';
import { generateDriveUrl } from './drive-search-location';

import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import AddToDrive from '@mui/icons-material/AddToDrive';
import PeopleOutlineOutlined from '@mui/icons-material/PeopleOutlineOutlined';

interface DriveLocationBreadcrumbProps {
  doc: DocLocation;
  loggedInUser: string;
  primaryDomain: string;
  isCurrent?: boolean;
}

export default function DriveLocationBreadcrumb({
  doc,
  loggedInUser,
  primaryDomain,
  isCurrent = false,
}: DriveLocationBreadcrumbProps) {
  const url = generateDriveUrl(
    doc.mimeType,
    doc.id,
    loggedInUser,
    primaryDomain
  );
  const isSharedWithMe = !!doc.isSharedWithMe;
  const isSharedDrive = !!doc.isSharedDrive;

  const hasAccess = doc.hasAccess == 'true';

  const iconSx = {
    width: '15px',
    height: '15px',
    ...(isCurrent && {
      filter: 'brightness(50%)',
    }),
  };

  const displayIcon = isSharedWithMe ? (
    <PeopleOutlineOutlined sx={iconSx} />
  ) : isSharedDrive || doc.name == 'My Drive' ? (
    <AddToDrive sx={iconSx} />
  ) : (
    <Box component="img" src={doc.iconLink} sx={iconSx} alt={doc.name} />
  );

  const breadcrumb = (
    <Stack
      component={hasAccess ? Link : Box}
      href={hasAccess ? url : undefined}
      target={hasAccess ? '_blank' : undefined}
      rel="noopener noreferrer"
      direction="row"
      alignItems="center"
      gap={1}
      maxWidth="150px"
      sx={{
        overflow: 'hidden',
        textDecoration: 'none',
        color: hasAccess ? 'inherit' : 'text.disabled',
        padding: '2px 4px',
        borderRadius: '4px',
        transition: (theme) => theme.transitions.create('background-color'),
        ...(hasAccess && {
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }),
      }}
    >
      {displayIcon}
      <Typography
        variant="body2"
        noWrap
        sx={{
          ...(isCurrent && { color: 'text.primary', fontWeight: 'medium' }),
        }}
      >
        {doc.name}
      </Typography>
    </Stack>
  );

  const tooltipTitle = hasAccess
    ? doc.name
    : `You don't have access on ${doc.name}`;

  return <Tooltip title={tooltipTitle}>{breadcrumb}</Tooltip>;
}
